import React, { useContext, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ShieldCheck, LogOut, Eye, EyeOff, Check, X } from 'lucide-react';

// Mirrors backend policy in routers/me.py (_validate_new_password) EXACTLY:
// at least 8 characters, at least one letter, at least one digit.
const rules = [
  { key: 'len', label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { key: 'letter', label: 'Contains a letter', test: (v) => /[A-Za-z]/.test(v) },
  { key: 'digit', label: 'Contains a number', test: (v) => /\d/.test(v) },
];

const PasswordField = ({ id, placeholder, value, onChange, show, onToggle }) => (
  <div className="relative">
    <Input
      id={id}
      data-testid={id}
      type={show ? 'text' : 'password'}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      autoComplete="new-password"
      className="pr-11 h-11 text-base"
    />
    <button
      type="button"
      data-testid={`${id}-eye`}
      onClick={onToggle}
      aria-label={show ? 'Hide password' : 'Show password'}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-[#0E2A47]"
    >
      {show ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
);

const ForcePasswordChange = () => {
  const { token, login, logout } = useContext(AuthContext);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const passed = rules.map((r) => ({ ...r, ok: r.test(next) }));
  const allRulesOk = passed.every((r) => r.ok);
  const matches = confirm.length > 0 && next === confirm;
  const canSubmit = !!current && allRulesOk && matches && !saving;

  const submit = async () => {
    if (!allRulesOk) return toast.error('New password does not meet the requirements');
    if (next !== confirm) return toast.error('New passwords do not match');
    setSaving(true);
    try {
      const res = await axios.post(`${API}/me/change-password`,
        { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${token}` } });
      // Adopt the fresh token/session so we continue straight into the app —
      // the user is never bounced back to the login screen.
      if (res.data?.token && res.data?.user) login(res.data.token, res.data.user);
      toast.success('Password updated — signing you in');
      window.location.assign('/select-portal');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not change password');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#0E2A47] flex items-center justify-center p-4 overflow-y-auto" style={{ minHeight: '100dvh' }} data-testid="force-password-change">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white p-6 shadow-xl sm:p-8" style={{ marginTop: 'env(safe-area-inset-top)', marginBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-[#8FC93A]/20 text-[#4d7c0f]">
          <ShieldCheck size={22} />
        </div>
        <h1 className="mt-3 text-center text-xl font-bold text-[#0E2A47] sm:text-2xl">Set a New Password</h1>
        <p className="mt-1 text-center text-sm text-slate-500">
          Your current password is temporary. Choose a new password before continuing.
        </p>
        <div className="mt-5 space-y-3">
          <PasswordField id="fpc-current" placeholder="Current (temporary) password"
            value={current} onChange={(e) => setCurrent(e.target.value)}
            show={showCurrent} onToggle={() => setShowCurrent((s) => !s)} />
          <PasswordField id="fpc-new" placeholder="New password"
            value={next} onChange={(e) => setNext(e.target.value)}
            show={showNext} onToggle={() => setShowNext((s) => !s)} />
          <PasswordField id="fpc-confirm" placeholder="Confirm new password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            show={showConfirm} onToggle={() => setShowConfirm((s) => !s)} />

          <ul className="space-y-1.5 rounded-lg bg-slate-50 p-3" data-testid="fpc-requirements">
            {passed.map((r) => (
              <li key={r.key} className={`flex items-center gap-2 text-xs ${r.ok ? 'text-[#4d7c0f]' : 'text-slate-500'}`}
                data-testid={`fpc-rule-${r.key}`}>
                {r.ok ? <Check size={14} /> : <X size={14} className="text-slate-300" />} {r.label}
              </li>
            ))}
            <li className={`flex items-center gap-2 text-xs ${matches ? 'text-[#4d7c0f]' : 'text-slate-500'}`}
              data-testid="fpc-rule-match">
              {matches ? <Check size={14} /> : <X size={14} className="text-slate-300" />} New passwords match
            </li>
          </ul>

          <Button onClick={submit} disabled={!canSubmit}
            data-testid="fpc-submit" className="w-full bg-[#8FC93A] text-[#0E2A47] hover:brightness-95">
            {saving ? 'Saving…' : 'Update Password & Continue'}
          </Button>
          <button onClick={logout} data-testid="fpc-logout"
            className="mx-auto flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForcePasswordChange;
